import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { insertDataMultipart, parsePdfFile, fetchSqlData, updateData } from '../../../services/api';
import { Button, Upload, message, Form, Alert, InputNumber, Tooltip, Modal, Checkbox, Drawer } from 'antd';
import { UploadOutlined, ScissorOutlined, UndoOutlined, RedoOutlined, EyeOutlined } from '@ant-design/icons';
import { MdDraw, MdOutlineInput, MdOutlineRemoveShoppingCart, MdSettingsBackupRestore } from 'react-icons/md';
import { LuImageUpscale } from 'react-icons/lu';
import { AiOutlinePushpin, AiOutlineQuestionCircle, AiOutlineRadiusUpright } from 'react-icons/ai';
import { CiAlignBottom, CiAlignCenterH, CiAlignCenterV, CiAlignLeft, CiAlignRight, CiAlignTop, CiEdit } from 'react-icons/ci';
import { CgDistributeHorizontal, CgDistributeVertical } from 'react-icons/cg';
import { RiExpandHeightFill, RiExpandWidthFill, RiMergeCellsHorizontal } from 'react-icons/ri';
import { TbArrowAutofitWidth } from 'react-icons/tb';
import { renderPdfPageToSvg, RenderPdfSvgResult } from '../renderPdfPageToSvg';
import { Bounds, fitSelectedToFrame as fitToFrameLib, alignSelected as alignLib, distributeSelected as distributeLib, equalizeSelected as equalizeLib, nudgeFields as nudgeLib } from '../PdfFormCreationFiles/PdfFormEnhancement';
import { buildSvgGuides, buildSvgGuidesWithSupportRects, excludeSuppressedGuides } from '../PdfFormCreationFiles/PdfGuideExtract';
import { useAutoFrames } from '../PdfFormCreationFiles/useAutoFrames';
import AutoFramesOverlay from '../PdfFormCreationFiles/AutoFramesOverlay';
import GuidesOverlay, { guideKey, parseGuideKey } from '../PdfFormCreationFiles/GuidesOverlay';
import { usePdfFormInteractions } from '../PdfFormCreationFiles/usePdfFormInteractions';
import { useLocation } from 'react-router-dom';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';
import MarqueeSelection from '../PdfFormCreationFiles/MarqueeSelection';
import { useMarqueeSelection } from '../PdfFormCreationFiles/useMarqueeSelection';
import FormFieldRenderer from '../PdfFormCreationFiles/FormFieldRenderer';
import ContextMenu from '../PdfFormCreationFiles/ContextMenu';
import FieldEditModal from '../PdfFormCreationFiles/FieldEditModal';
import FormMetaSection from '../PdfFormCreationFiles/FormMetaSection';
import { getFormGeneratorInfoForPath } from '../PdfFormCreationFiles/formUtils';
import { Field, FormJson, MetaFields } from '../PdfFormCreationFiles/constants';
import { decodeSvgFile, encodeSvgFileForDb } from '../PdfFormCreationFiles/svgFileCodec';
import { useFormGroups } from '../PdfFormCreationFiles/useFormGroups';
import { useFieldManagement } from '../PdfFormCreationFiles/useFieldManagement';
import { svgElementFromMarkup, useFormHistory } from '../PdfFormCreationFiles/useFormHistory';

const FORM_PREVIEW_STORAGE_KEY = 'form_preview_payload_v1';

function clipSvgToDisplayRect(
  svg: SVGSVGElement,
  clip: { x0: number; y0: number; x1: number; y1: number },
  displaySize: { width: number; height: number }
): { svg: SVGSVGElement; width: number; height: number } {
  const x0 = Math.max(0, Math.min(clip.x0, clip.x1));
  const y0 = Math.max(0, Math.min(clip.y0, clip.y1));
  const x1 = Math.min(displaySize.width, Math.max(clip.x0, clip.x1));
  const y1 = Math.min(displaySize.height, Math.max(clip.y0, clip.y1));
  const dw = Math.max(1, x1 - x0);
  const dh = Math.max(1, y1 - y0);

  const clone = svg.cloneNode(true) as SVGSVGElement;
  const vbAttr = clone.getAttribute('viewBox');
  let vx = 0;
  let vy = 0;
  let vbW = displaySize.width;
  let vbH = displaySize.height;
  if (vbAttr) {
    const p = vbAttr.trim().split(/\s+/).map(Number);
    if (p.length === 4 && p.every((n) => !isNaN(n))) {
      [vx, vy, vbW, vbH] = p;
    }
  }
  const scaleX = vbW / (displaySize.width || 1);
  const scaleY = vbH / (displaySize.height || 1);
  const ux = vx + x0 * scaleX;
  const uy = vy + y0 * scaleY;
  const uw = dw * scaleX;
  const uh = dh * scaleY;

  const ns = 'http://www.w3.org/2000/svg';
  const g = document.createElementNS(ns, 'g');
  g.setAttribute('transform', `translate(${-ux}, ${-uy})`);
  const keepTags = new Set(['defs', 'clippath', 'mask', 'style', 'title', 'desc', 'metadata']);
  Array.from(clone.childNodes).forEach((node) => {
    if (node.nodeType === 1 && keepTags.has((node as Element).tagName.toLowerCase())) return;
    g.appendChild(node);
  });
  clone.appendChild(g);
  clone.setAttribute('width', String(dw));
  clone.setAttribute('height', String(dh));
  clone.setAttribute('viewBox', `0 0 ${uw} ${uh}`);
  clone.style.width = `${dw}px`;
  clone.style.height = `${dh}px`;
  clone.style.pointerEvents = 'none';
  return { svg: clone, width: dw, height: dh };
}

const helpSectionStyle: React.CSSProperties = { marginBottom: 16 };
const helpTitleStyle: React.CSSProperties = { margin: '0 0 6px', fontSize: 13, fontWeight: 600 };
const helpListStyle: React.CSSProperties = { margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55 };

const HelpIconRow: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8, fontSize: 13, lineHeight: 1.45 }}>
    <span style={{ flex: '0 0 22px', height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {icon}
    </span>
    <span>{children}</span>
  </div>
);

const FormBuilderHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Drawer title="Form builder help" placement="right" width={420} open={open} onClose={onClose}>
    <section style={helpSectionStyle}>
      <h4 style={helpTitleStyle}>Toolbar</h4>
      <HelpIconRow icon={<UploadOutlined />}>Loads the PDF used as the form background.</HelpIconRow>
      <HelpIconRow icon={<span style={{ fontSize: 11, fontWeight: 600 }}>Save</span>}>Create or Update saves the form definition.</HelpIconRow>
      <HelpIconRow icon={<AiOutlineQuestionCircle size={16} />}>Opens this help.</HelpIconRow>
      <HelpIconRow icon={<LuImageUpscale size={16} />}>Changes the saved page size and scales every field. Font scaling is optional.</HelpIconRow>
      <HelpIconRow icon={<UndoOutlined />}>Undo. Also covers scale and clip. Ctrl+Z.</HelpIconRow>
      <HelpIconRow icon={<RedoOutlined />}>Redo. Ctrl+Y or Ctrl+Shift+Z.</HelpIconRow>
      <HelpIconRow icon={<EyeOutlined />}>Opens the form in a new tab.</HelpIconRow>
      <HelpIconRow icon={<MdDraw size={16} />}>Drag on the page to add a field. Click again to leave draw mode.</HelpIconRow>
      <HelpIconRow icon={<ScissorOutlined />}>Drag a rectangle to crop the page. Fields shift with the crop.</HelpIconRow>
      <HelpIconRow icon={<AiOutlineRadiusUpright size={16} />}>Shows detected guide lines. Click lines, then Delete, to ignore them.</HelpIconRow>
      <HelpIconRow icon={<MdOutlineInput size={16} />}>Shows detected boxes you can turn into fields.</HelpIconRow>
      <HelpIconRow icon={<AiOutlinePushpin size={16} />}>Creates fields from the selected auto-frames. Visible only while auto-frames are on.</HelpIconRow>
      <HelpIconRow icon={<span style={{ fontSize: 11, fontWeight: 600 }}>Inset</span>}>Shrink, in page units, used by Fit to Frame.</HelpIconRow>
    </section>
    <section style={helpSectionStyle}>
      <h4 style={helpTitleStyle}>Right-click align</h4>
      <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.45 }}>Right-click a field. Align, size, and merge need at least two. Distribute needs three.</p>
      <HelpIconRow icon={<TbArrowAutofitWidth size={16} />}>Fit the selection to the nearest frame. Uses the Inset value.</HelpIconRow>
      <HelpIconRow icon={<CiEdit size={16} />}>Edit properties of every selected field.</HelpIconRow>
      <HelpIconRow icon={<RiMergeCellsHorizontal size={16} />}>Merge the selection into one rectangle.</HelpIconRow>
      <HelpIconRow icon={<CiAlignLeft size={16} />}>Align left edges.</HelpIconRow>
      <HelpIconRow icon={<CiAlignRight size={16} />}>Align right edges.</HelpIconRow>
      <HelpIconRow icon={<CiAlignTop size={16} />}>Align top edges.</HelpIconRow>
      <HelpIconRow icon={<CiAlignBottom size={16} />}>Align bottom edges.</HelpIconRow>
      <HelpIconRow icon={<CiAlignCenterH size={16} />}>Align horizontal centers.</HelpIconRow>
      <HelpIconRow icon={<CiAlignCenterV size={16} />}>Align vertical centers.</HelpIconRow>
      <HelpIconRow icon={<RiExpandWidthFill size={16} />}>Make selected fields the same width.</HelpIconRow>
      <HelpIconRow icon={<RiExpandHeightFill size={16} />}>Make selected fields the same height.</HelpIconRow>
      <HelpIconRow icon={<CgDistributeHorizontal size={16} />}>Distribute horizontal spacing. Needs three or more fields.</HelpIconRow>
      <HelpIconRow icon={<CgDistributeVertical size={16} />}>Distribute vertical spacing. Needs three or more fields.</HelpIconRow>
    </section>
    <section style={helpSectionStyle}>
      <h4 style={helpTitleStyle}>Keyboard</h4>
      <ul style={helpListStyle}>
        <li><b>Ctrl+Z</b> undo. <b>Ctrl+Y</b> or <b>Ctrl+Shift+Z</b> redo. While typing in a field, Ctrl+Z edits the text instead.</li>
        <li><b>Ctrl+C</b> copy, <b>Ctrl+V</b> paste at the pointer.</li>
        <li><b>Delete</b> removes selected fields. With frames or guides selected, it removes those instead.</li>
        <li><b>Esc</b> clears the selection. It does not close the field editor.</li>
        <li><b>Arrow keys</b> nudge 1 unit. <b>Shift</b> is 10, <b>Ctrl</b> is 0.5, <b>Ctrl+Shift</b> is 5.</li>
        <li><b>Ctrl</b> while dragging or resizing locks movement to one axis.</li>
        <li><b>Ctrl</b> or <b>Shift</b> click adds to the selection. Drag on empty space to marquee-select.</li>
        <li><b>Ctrl+scroll</b> zooms the view only. It does not change the saved size.</li>
      </ul>
    </section>
    <section style={helpSectionStyle}>
      <h4 style={helpTitleStyle}>Fields</h4>
      <ul style={helpListStyle}>
        <li>Drag a field to move it. Drag the corner to resize it.</li>
        <li>Click the field name to edit its type, options, and required flag.</li>
        <li>Right-click a selection for Fit to Frame, align, same size, distribute, merge, and bulk edit.</li>
      </ul>
    </section>
  </Drawer>
);

const DynamicFormOverlay: React.FC<{
  formJson: FormJson;
  metaFields: MetaFields;
  pdfFile: File | null;
  pdfFileName: string | null;
  onResult: (result: { success: boolean; message: string }) => void;
  tableName: string;
  selectedGroupId: number | null;
  groupEssentialFields: string[];
  // edit support
  mode?: 'create' | 'edit';
  definitionId?: string;
  toolbarSlot?: HTMLElement | null;
}> = ({ formJson, metaFields, pdfFile, pdfFileName, onResult, tableName, selectedGroupId, groupEssentialFields, mode = 'create', definitionId, toolbarSlot = null }) => {
  const [imgSize, setImgSize] = useState<{ width: number; height: number }>({ width: formJson.pdfWidth || 600, height: formJson.pdfHeight || 800 });
  const [zoom, setZoom] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);
  const [svgElement, setSvgElement] = useState<SVGSVGElement | null>(null);
  // removed unused svgViewBox state
  const svgContainerRef = useRef<HTMLDivElement | null>(null);
  const svgMountRef = useRef<HTMLDivElement | null>(null);
  const [fieldValues, setFieldValues] = useState<{[key: string]: string | boolean}>({});
  const [showDetectedFrames, setShowDetectedFrames] = useState<boolean>(false);
  const [showGuidesOverlay, setShowGuidesOverlay] = useState<boolean>(false);
  const [extractedGuides, setExtractedGuides] = useState<{ x: number[]; y: number[] } | null>(null);
  const [suppressedGuides, setSuppressedGuides] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });
  const [selectedGuideKeys, setSelectedGuideKeys] = useState<Set<string>>(new Set());
  const guidesOverlay = useMemo(
    () => (extractedGuides ? excludeSuppressedGuides(extractedGuides, suppressedGuides) : null),
    [extractedGuides, suppressedGuides]
  );
  const {
    localFields,
    setLocalFields,
    localFieldsWithRequired,
    editingFieldIdx,
    bulkEditingIdxs,
    modalFieldName,
    modalFieldType,
    modalRequired,
    modalComponent,
    modalOptionsInput,
    modalFontSize,
    modalTextAlign,
    modalOptions,
    duplicateFieldError,
    duplicateNames,
    hasDuplicateFieldNames,
    handleFieldNameIconClick,
    handleModalOk,
    handleModalCancel,
    handleModalInputChange,
    handleModalRequiredChange,
    setModalFieldType,
    setModalComponent,
    setModalOptionsInput,
    setModalFontSize,
    setModalTextAlign,
    handleDeleteField,
    openBulkEdit,
    bindFieldWriter,
  } = useFieldManagement(formJson.fields, groupEssentialFields);

  const svgMarkupRef = useRef<SVGSVGElement | null>(svgElement);
  svgMarkupRef.current = svgElement;
  const getSvgMarkup = useCallback(() => {
    const el = svgMarkupRef.current;
    if (!el) return null;
    try {
      return new XMLSerializer().serializeToString(el);
    } catch {
      return null;
    }
  }, []);
  const setSvgMarkup = useCallback((markup: string) => {
    const el = svgElementFromMarkup(markup);
    if (el) setSvgElement(el);
  }, []);
  const {
    setFieldsSilent,
    updateFields,
    checkpoint,
    beginGesture,
    endGesture,
    undo,
    redo,
    clearHistory,
    canUndo,
    canRedo,
  } = useFormHistory<Field>({
    fields: localFields,
    setFields: setLocalFields,
    imgSize,
    setImgSize,
    getSvgMarkup,
    setSvgMarkup,
    suppressedGuides,
    setSuppressedGuides,
  });
  bindFieldWriter(updateFields);

  const formRef = useRef<HTMLFormElement | null>(null);
  const { frames: detectedFrames, selectedFrameIdxs, setSelectedFrameIdxs, frameLikeFields, recompute: recomputeFrames, removeFramesByIdxs } = useAutoFrames({ svgElement, formEl: formRef.current, fields: localFields, imgSize, minWidth: 30, minHeight: 5, suppressedGuides });

  // removed unused fieldNameInputRef
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [selectedIdxs, setSelectedIdxs] = useState<Set<number>>(new Set());
  // removed unused lastSelectedIdx
  const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; open: boolean }>({ x: 0, y: 0, open: false });
  const contextRef = useRef<HTMLDivElement | null>(null);
  const hoveredRef = useRef<boolean>(false);
  // Prevent double execution (e.g., due to double click or development StrictMode re-renders)
  const creatingRef = useRef<boolean>(false);
  // Option B: scale the *real* form size (pdfWidth/pdfHeight) and scale all elements (field rectangles + font sizes)
  const [helpOpen, setHelpOpen] = useState(false);
  const [scaleModalOpen, setScaleModalOpen] = useState(false);
  const [scalePercent, setScalePercent] = useState<number>(100);
  const [scaleFonts, setScaleFonts] = useState<boolean>(true);
  // Drawing mode state
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [isClipMode, setIsClipMode] = useState(false);
  const [inset, setInset] = useState<number>(0.5);
  // Drawing rectangle state for visual feedback
  // removed unused drawingRect state
  const drawingLike = isDrawingMode || isClipMode;
  const bounds: Bounds = { 
    maxW: imgSize.width, 
    maxH: imgSize.height,
    width: imgSize.width,
    height: imgSize.height
  };

  const applyFormScale = useCallback((factor: number, scaleFontSizes: boolean) => {
    const f = Number(factor);
    if (!Number.isFinite(f) || f <= 0) return;
    if (Math.abs(f - 1) < 0.0001) return;

    checkpoint({ captureSvg: true });
    const nextW = imgSize.width * f;
    const nextH = imgSize.height * f;

    setFieldsSilent((prev) =>
      (prev || []).map((field) => {
        const p = field?.position;
        const next: any = { ...field };
        if (p) {
          next.position = {
            ...p,
            x0: (p.x0 ?? 0) * f,
            y0: (p.y0 ?? 0) * f,
            x1: (p.x1 ?? 0) * f,
            y1: (p.y1 ?? 0) * f,
          };
        }
        if (scaleFontSizes && typeof (field as any).fontsize === 'number') {
          const raw = (field as any).fontsize * f;
          next.fontsize = Math.max(6, Math.min(72, Math.round(raw)));
        }
        return next as Field;
      })
    );

    setImgSize({ width: nextW, height: nextH });

    // Keep viewBox unchanged; resize the rendered SVG so it scales visually in the same coordinate space as fields.
    setSvgElement((prev) => {
      if (!prev) return prev;
      const next = prev.cloneNode(true) as SVGSVGElement;
      next.setAttribute('width', String(nextW));
      next.setAttribute('height', String(nextH));
      next.style.width = `${nextW}px`;
      next.style.height = `${nextH}px`;
      next.style.pointerEvents = 'none';
      return next;
    });

    // Clear selection to avoid confusion after scaling
    setSelectedIdxs(new Set());
    setFocusedIdx(null);
    setSelectedFrameIdxs(new Set());
    setSelectedGuideKeys(new Set());
    setContextMenu(p => ({ ...p, open: false }));
  }, [imgSize.width, imgSize.height, checkpoint, setFieldsSilent, setContextMenu, setFocusedIdx, setSelectedFrameIdxs, setSelectedGuideKeys, setSelectedIdxs]);

  // Helpers for dragging (stay in PDF coordinates even when visually zoomed)
  const getContainerPoint = useCallback((e: MouseEvent | React.MouseEvent): { x: number; y: number } => {
    const el = formRef.current;
    const rect = el ? el.getBoundingClientRect() : ({ left: 0, top: 0 } as DOMRect);
    const z = zoom || 1;
    return {
      x: ((e as MouseEvent).clientX - rect.left) / z,
      y: ((e as MouseEvent).clientY - rect.top) / z,
    };
  }, [zoom]);

  const toggleGuideSelection = useCallback((axis: 'x' | 'y', value: number, e: React.MouseEvent) => {
    const key = guideKey(axis, value);
    const multi = e.ctrlKey || e.metaKey || e.shiftKey;
    setSelectedGuideKeys(prev => {
      if (!multi) return new Set([key]);
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    setSelectedIdxs(new Set());
    setSelectedFrameIdxs(new Set());
  }, [setSelectedFrameIdxs]);

  const removeSelectedGuides = useCallback(() => {
    if (selectedGuideKeys.size === 0) return;
    setSuppressedGuides(prev => {
      const next = { x: prev.x.slice(), y: prev.y.slice() };
      selectedGuideKeys.forEach(key => {
        const parsed = parseGuideKey(key);
        if (!parsed) return;
        const list = parsed.axis === 'x' ? next.x : next.y;
        if (!list.some(v => Math.abs(v - parsed.value) <= 1.5)) list.push(parsed.value);
      });
      return next;
    });
    setSelectedGuideKeys(new Set());
    try { message.success('Removed selected guides from frame detection'); } catch {}
  }, [selectedGuideKeys]);

  const restoreRemovedGuides = useCallback(() => {
    setSuppressedGuides({ x: [], y: [] });
    setSelectedGuideKeys(new Set());
    try { message.success('Restored all removed guides'); } catch {}
  }, []);

  const mergeSelectedFields = useCallback(() => {
    const idxs = Array.from(selectedIdxs);
    if (idxs.length < 2) return;

    type Pos = { x0: number; y0: number; x1: number; y1: number; page?: number };
    const fields = localFields;
    const selected = idxs
      .map(i => ({ idx: i, field: fields[i] }))
      .filter(x => !!x.field && !!x.field.position) as { idx: number; field: Field & { position: Pos } }[];

    if (selected.length < 2) {
      message.warning('Select at least 2 fields with positions to merge.');
      return;
    }

    const pages = new Set(selected.map(s => s.field.position?.page ?? 1));
    if (pages.size > 1) {
      message.error('Merge requires all selected fields to be on the same page.');
      return;
    }

    const boundsRect = selected.reduce(
      (acc, s) => {
        const p = s.field.position;
        return {
          x0: Math.min(acc.x0, Math.min(p.x0, p.x1)),
          y0: Math.min(acc.y0, Math.min(p.y0, p.y1)),
          x1: Math.max(acc.x1, Math.max(p.x0, p.x1)),
          y1: Math.max(acc.y1, Math.max(p.y0, p.y1)),
        };
      },
      { x0: Number.POSITIVE_INFINITY, y0: Number.POSITIVE_INFINITY, x1: Number.NEGATIVE_INFINITY, y1: Number.NEGATIVE_INFINITY }
    );

    // Relaxed rectangle check:
    // You only require that the outer boundary aligns (same left edge for leftmost fields,
    // same top edge for topmost fields, etc.), not that the inside is fully tiled.
    const EPS = 0.001;
    const near = (a: number, b: number) => Math.abs(a - b) <= EPS;

    const left = boundsRect.x0;
    const right = boundsRect.x1;
    const top = boundsRect.y0;
    const bottom = boundsRect.y1;

    const touchesLeft = selected.some(s => {
      const p = s.field.position;
      return near(Math.min(p.x0, p.x1), left);
    });
    const touchesRight = selected.some(s => {
      const p = s.field.position;
      return near(Math.max(p.x0, p.x1), right);
    });
    const touchesTop = selected.some(s => {
      const p = s.field.position;
      return near(Math.min(p.y0, p.y1), top);
    });
    const touchesBottom = selected.some(s => {
      const p = s.field.position;
      return near(Math.max(p.y0, p.y1), bottom);
    });

    if (!touchesLeft || !touchesRight || !touchesTop || !touchesBottom) {
      message.error('Merge requires the selection boundary to align as a rectangle (must touch left/right/top/bottom).');
      return;
    }

    if (Math.abs(right - left) < 1 || Math.abs(bottom - top) < 1) {
      message.error('Merge failed: selection rectangle is too small.');
      return;
    }

    // Choose top-left field (smallest y0, then smallest x0)
    const topLeft = [...selected].sort((a, b) => {
      const pa = a.field.position;
      const pb = b.field.position;
      const ay = Math.min(pa.y0, pa.y1);
      const by = Math.min(pb.y0, pb.y1);
      if (Math.abs(ay - by) > EPS) return ay - by;
      const ax = Math.min(pa.x0, pa.x1);
      const bx = Math.min(pb.x0, pb.x1);
      if (Math.abs(ax - bx) > EPS) return ax - bx;
      return a.idx - b.idx;
    })[0];

    const page = (topLeft.field.position?.page ?? 1);
    const mergedField: Field = {
      ...topLeft.field,
      position: { x0: boundsRect.x0, y0: boundsRect.y0, x1: boundsRect.x1, y1: boundsRect.y1, page },
    };

    // New field is appended after removing selected ones
    const newIdx = Math.max(0, localFields.length - selected.length);
    updateFields(prev => {
      const toRemove = new Set(selected.map(s => s.idx));
      const next: Field[] = [];
      prev.forEach((f, i) => {
        if (!toRemove.has(i)) next.push(f);
      });
      next.push(mergedField);
      return next;
    });

    // select/focus the newly created merged field (last index)
    setSelectedIdxs(new Set([newIdx]));
    setFocusedIdx(newIdx);
    message.success('Merged fields into one.');
  }, [localFields, selectedIdxs, updateFields, setSelectedIdxs, setFocusedIdx]);

  const applyClipRect = useCallback((rect: { x0: number; y0: number; x1: number; y1: number }) => {
    if (!svgElement) {
      message.warning('Nothing to clip yet.');
      return;
    }
    const nx0 = Math.min(rect.x0, rect.x1);
    const ny0 = Math.min(rect.y0, rect.y1);
    const nx1 = Math.max(rect.x0, rect.x1);
    const ny1 = Math.max(rect.y0, rect.y1);
    const dw = nx1 - nx0;
    const dh = ny1 - ny0;
    if (dw < 8 || dh < 8) {
      message.info('Clip region is too small.');
      return;
    }
    const clipped = clipSvgToDisplayRect(svgElement, { x0: nx0, y0: ny0, x1: nx1, y1: ny1 }, imgSize);
    checkpoint({ captureSvg: true });
    setSvgElement(clipped.svg);
    setFieldsSilent((prev) => prev.map((f) => {
      const p = f.position;
      if (!p) return f;
      return {
        ...f,
        position: {
          ...p,
          x0: (p.x0 ?? 0) - nx0,
          y0: (p.y0 ?? 0) - ny0,
          x1: (p.x1 ?? 0) - nx0,
          y1: (p.y1 ?? 0) - ny0,
        },
      };
    }));
    setImgSize({ width: clipped.width, height: clipped.height });
    setSuppressedGuides({ x: [], y: [] });
    setSelectedGuideKeys(new Set());
    setExtractedGuides(null);
    setSelectedFrameIdxs(new Set());
    setSelectedIdxs(new Set());
    setFocusedIdx(null);
    setIsClipMode(false);
    message.success('Form clipped. Create/Update to keep this crop.');
  }, [svgElement, imgSize, checkpoint, setFieldsSilent, setSelectedFrameIdxs]);

  // Callback for creating a new field from drawn rectangle
  const createFieldCallback = useCallback((rect: { x0: number; y0: number; x1: number; y1: number }) => {
    if (isClipMode) {
      applyClipRect(rect);
      return;
    }
    updateFields(prev => {
      const index = prev.length;
      const newField: Field = {
        name: `field_${index + 1}`,
        label: `Field ${index + 1}`,
        type: 'text',
        position: { x0: Math.min(rect.x0, rect.x1), y0: Math.min(rect.y0, rect.y1), x1: Math.max(rect.x0, rect.x1), y1: Math.max(rect.y0, rect.y1), page: 1 },
        fontsize: 12,
      };
      // Select and focus the newly created field
      setSelectedIdxs(new Set([index]));
      setFocusedIdx(index);
      message.success('Field created');
      return [...prev, newField];
    });
  }, [isClipMode, applyClipRect, updateFields]);

  // Use the enhanced interactions hook with mouse handlers and copy-paste
  const { dragging, resizing, drawing, startDrag, startResize, startDrawing, updateDrawing, finishDrawing, cancel, mouseHandlers, copiedFields, copySelectedFields, pasteFields } = usePdfFormInteractions({
    fields: localFields,
    setFields: setFieldsSilent,
    selectedIdxs,
    setSelectedIdxs,
    bounds, // Add this
    options: { axisLockWithCtrl: true },
    isDrawingMode: drawingLike,
    getContainerPoint,
    createFieldCallback,
    cursorPos,
    beginGesture,
    endGesture,
    commitFields: updateFields,
  });

  // removed inline auto-frames computation (moved to useAutoFrames)

  // Recompute frames when fields change while overlay is visible
  useEffect(() => {
    if (showDetectedFrames) recomputeFrames();
  }, [showDetectedFrames, localFields, imgSize.width, imgSize.height, recomputeFrames]);

  // Global Delete fallback: ensure Delete works even if focus is not on the form
  useEffect(() => {
    if (!showDetectedFrames && !showGuidesOverlay) return;
    const onKey = (e: KeyboardEvent) => {
      const key = e.key;
      if (key !== 'Delete' && key !== 'Backspace') return;
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      const isEditing = tag === 'input' || tag === 'textarea' || (document.activeElement as HTMLElement | null)?.isContentEditable;
      if (isEditing) return;
      if (showDetectedFrames && selectedFrameIdxs.size > 0) {
        removeFramesByIdxs(selectedFrameIdxs);
        setSelectedFrameIdxs(new Set());
        try { message.success('Removed selected auto-frames'); } catch {}
        e.preventDefault();
        return;
      }
      if (showGuidesOverlay && selectedGuideKeys.size > 0) {
        removeSelectedGuides();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDetectedFrames, showGuidesOverlay, selectedFrameIdxs, selectedGuideKeys, removeFramesByIdxs, setSelectedFrameIdxs, removeSelectedGuides]);

  // Use keyboard shortcuts hook
  useKeyboardShortcuts([
    { key: 'c', ctrl: true, handler: copySelectedFields },
    { key: 'v', ctrl: true, handler: pasteFields },
  ]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || target.isContentEditable) return;
      }
      if (editingFieldIdx !== null || (bulkEditingIdxs?.length || 0) >= 2) return;
      e.preventDefault();
      if (isUndo) undo();
      else redo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo, editingFieldIdx, bulkEditingIdxs]);

  // While hovering the PDF area, prevent browser-level zoom (Ctrl+wheel, Ctrl+ +/-)
  useEffect(() => {
    const onWheel = (e: WheelEvent) => {
      if (!hoveredRef.current) return;
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (!hoveredRef.current) return;
      if (e.ctrlKey) {
        const k = e.key;
        if (k === '+' || k === '=' || k === '-' || k === '_' || k === '0') {
          e.preventDefault();
        }
      }
    };
    window.addEventListener('wheel', onWheel, { passive: false } as any);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('wheel', onWheel as any);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!contextMenu.open) return;
    const onDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (contextRef.current && !contextRef.current.contains(target)) {
        setContextMenu(prev => ({ ...prev, open: false }));
      }
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setContextMenu(prev => ({ ...prev, open: false }));
    };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu.open]);

  // Ensure Escape clears selection even if focus is inside an input rendered over the PDF
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      // If the edit modal is open, let it handle Escape
      if (editingFieldIdx !== null || (bulkEditingIdxs?.length || 0) >= 2) return;

      const hadSelection = selectedIdxs.size > 0 || selectedFrameIdxs.size > 0 || selectedGuideKeys.size > 0 || contextMenu.open;
      if (!hadSelection) return;

      setSelectedIdxs(new Set());
      setFocusedIdx(null);
      setSelectedFrameIdxs(new Set());
      setSelectedGuideKeys(new Set());
      setContextMenu(p => ({ ...p, open: false }));

      const ae = document.activeElement as HTMLElement | null;
      if (ae && typeof ae.blur === 'function') ae.blur();
      if (formRef.current) {
        try { formRef.current.focus(); } catch {}
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedIdxs, selectedFrameIdxs, selectedGuideKeys, contextMenu.open, editingFieldIdx, bulkEditingIdxs, setSelectedFrameIdxs]);

  // Keyboard nudging (arrow keys, Shift=10px)
  const nudgeField = (idx: number, dx: number, dy: number) => {
    updateFields(prev => {
      const indices = selectedIdxs.size > 1 && selectedIdxs.has(idx) ? Array.from(selectedIdxs) : [idx];
      const bounds: Bounds = { 
        maxW: imgSize.width, 
        maxH: imgSize.height,
        width: imgSize.width,
        height: imgSize.height
      };
      return nudgeLib(prev, indices, dx, dy, bounds);
    }, { coalesceMs: 400 });
  };
  
  // Resize selected fields to the nearest surrounding frame guides
  const fitSelectedToFrame = () => {
    const idxs = Array.from(selectedIdxs);
    if (idxs.length === 0) return;
    const { guides: extracted, supportRects } = buildSvgGuidesWithSupportRects(svgElement as unknown as SVGSVGElement | null, formRef.current);
    const guides = excludeSuppressedGuides(extracted, suppressedGuides);
    if (guides.x.length === 0 && guides.y.length === 0) {
      message.warning('No frame guides detected on the page.');
      return;
    }
    const bounds: Bounds = { 
      maxW: imgSize.width, 
      maxH: imgSize.height,
      width: imgSize.width,
      height: imgSize.height
    };
    const SNAP_THRESHOLD = 2; // fixed internal threshold in PDF units
    const { fields: updated, changed, snapped } = fitToFrameLib(localFields, idxs, bounds, guides, SNAP_THRESHOLD, Number(inset) || 0, supportRects);
    if (!snapped && !changed) {
      message.info('No nearby guides within threshold. Try moving closer to frame lines.');
      return;
    }
    updateFields(updated);
  };

  const handleNudgeKey = (idx: number) => (e: React.KeyboardEvent) => {
    // Don't handle key events for textareas if it's the Enter key
    if ((e.target as HTMLElement).tagName.toLowerCase() === 'textarea' && e.key === 'Enter') {
      return; // Let the textarea handle the Enter key
    }
    
    const step = e.ctrlKey && e.shiftKey ? 5 : (e.ctrlKey ? 0.5 : (e.shiftKey ? 10 : 1));
    let handled = false;
    switch (e.key) {
      case 'ArrowLeft':
        nudgeField(idx, -step, 0); handled = true; break;
      case 'ArrowRight':
        nudgeField(idx, step, 0); handled = true; break;
      case 'ArrowUp':
        nudgeField(idx, 0, -step); handled = true; break;
      case 'ArrowDown':
        nudgeField(idx, 0, step); handled = true; break;
    }
    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Selection helpers
  const toggleSelect = (e: React.MouseEvent, idx: number) => {
    // Only respond to left-click; right-click should not change current multi-selection
    if (e.button !== 0) return;
    const multi = e.ctrlKey || e.metaKey || e.shiftKey;
    setSelectedIdxs(prev => {
      const wasSelected = prev.has(idx);
      if (!multi && wasSelected && prev.size > 1) {
        // Preserve existing multi-selection when clicking an already-selected item
        return new Set(prev);
      }
      const next = new Set(prev);
      if (multi) {
        if (next.has(idx)) next.delete(idx); else next.add(idx);
      } else {
        next.clear(); next.add(idx);
      }
      return next;
    });
    // removed unused lastSelectedIdx update
    setFocusedIdx(idx);
  };

  // Alignment utilities
  // removed unused getFieldPos helper
  const alignSelected = (mode: 'left'|'right'|'top'|'bottom'|'hcenter'|'vcenter') => {
    const idxs = Array.from(selectedIdxs);
    if (idxs.length < 2) return;
    const bounds: Bounds = { 
      maxW: imgSize.width, 
      maxH: imgSize.height,
      width: imgSize.width,
      height: imgSize.height
    };
    updateFields(prev => alignLib(prev, idxs, bounds, mode));
  };

  const distributeSelected = (axis: 'h'|'v') => {
    const idxs = Array.from(selectedIdxs);
    if (idxs.length < 3) return;
    const bounds: Bounds = { 
      maxW: imgSize.width, 
      maxH: imgSize.height,
      width: imgSize.width,
      height: imgSize.height
    };
    updateFields(prev => distributeLib(prev, idxs, bounds, axis));
  };

  const equalizeSelected = (mode: 'w'|'h') => {
    const idxs = Array.from(selectedIdxs);
    if (idxs.length < 2) return;
    const bounds: Bounds = { 
      maxW: imgSize.width, 
      maxH: imgSize.height,
      width: imgSize.width,
      height: imgSize.height
    };
    updateFields(prev => equalizeLib(prev, idxs, bounds, mode));
  };

  useEffect(() => {
    setLocalFields(formJson.fields);
    clearHistory();
  }, [formJson.fields, setLocalFields, clearHistory]);


  // In edit mode, load existing definition and mount SVG background
  useEffect(() => {
    let cancelled = false;
    const loadDef = async () => {
      try {
        if (mode !== 'edit' || !definitionId) return;
        const arr = await fetchSqlData(tableName, 'id', definitionId);
        const rec = Array.isArray(arr) ? arr[0] : null;
        if (!rec || cancelled) return;
        const fj = (rec.form_json || {}) as any;
        const fieldsFromDb = Array.isArray(fj.fields) ? fj.fields : [];
        setLocalFields(fieldsFromDb);
        const svgHex = rec?.svg_file as string | undefined;
        if (svgHex) {
          let svgString = decodeSvgFile(svgHex);
          // Normalize SVG namespace like FormEdit.getSvgWithSize
          const cleanedSvgString = svgString.replace(/<svg:/g, '<').replace(/<\/svg:/g, '</');
          // Derive size from viewBox if present; fallback to form_json
          let vbW = fj.pdfWidth || 1000;
          let vbH = fj.pdfHeight || 1400;
          const match = cleanedSvgString.match(/viewBox="([^"]+)"/);
          if (match) {
            const parts = match[1].split(' ').map(Number);
            if (parts.length >= 4 && !isNaN(parts[2]) && !isNaN(parts[3])) {
              vbW = parts[2];
              vbH = parts[3];
            }
          }
          // Render size (what we show on screen / save as pdfWidth/pdfHeight)
          const targetW = Number(fj.pdfWidth) || vbW;
          const targetH = Number(fj.pdfHeight) || vbH;
          const parser = new DOMParser();
          const doc = parser.parseFromString(cleanedSvgString, 'image/svg+xml');
          const svgElem = doc.querySelector('svg');
          if (svgElem) {
            // Keep viewBox in its original coordinate system (vbW/vbH),
            // but size the rendered SVG using saved pdfWidth/pdfHeight (targetW/targetH).
            svgElem.setAttribute('width', String(targetW));
            svgElem.setAttribute('height', String(targetH));
            svgElem.style.width = `${targetW}px`;
            svgElem.style.height = `${targetH}px`;
            svgElem.setAttribute('viewBox', `0 0 ${vbW} ${vbH}`);
            (svgElem as SVGSVGElement).style.pointerEvents = 'none';
            setSvgElement(svgElem.cloneNode(true) as SVGSVGElement);
            setImgSize({ width: targetW, height: targetH });
          }
        }
        clearHistory();
      } catch {}
    };
    loadDef();
    return () => { cancelled = true; };
  }, [mode, definitionId, tableName, setLocalFields, clearHistory]);

  useEffect(() => {
    let isMounted = true;
    if (pdfFile) {
      renderPdfPageToSvg(pdfFile, 1, 3)
        .then(async (result: RenderPdfSvgResult) => {
          if (isMounted) {
            // Use SVG string directly, skip backend font patch endpoint
            let patchedSvgString = result.svgString;
            // Parse patched SVG string to SVGSVGElement
            const parser = new DOMParser();
            const doc = parser.parseFromString(patchedSvgString, 'image/svg+xml');
            const svgElem = doc.querySelector('svg');
            if (svgElem) {
              // PDF'in orijinal boyutunu kullan
              const pdfWidth = formJson.pdfWidth || 600;
              const pdfHeight = formJson.pdfHeight || 800;
              svgElem.setAttribute('width', String(pdfWidth));
              svgElem.setAttribute('height', String(pdfHeight));
              svgElem.style.width = pdfWidth + 'px';
              svgElem.style.height = pdfHeight + 'px';
              // Ensure container receives pointer events in drawing mode
              (svgElem as SVGSVGElement).style.pointerEvents = 'none';
              // clone to avoid attaching the same node to different parents across renders
              const cloned = svgElem.cloneNode(true) as SVGSVGElement;
              (cloned as SVGSVGElement).style.pointerEvents = 'none';
              setSvgElement(cloned);
              // svgViewBox removed
              setImgSize({ width: pdfWidth, height: pdfHeight });
            } else {
              setSvgElement(null);
            }
          }
        })
        .catch(() => setSvgElement(null));
    } else if (mode !== 'edit') {
      setSvgElement(null);
    }
    return () => { isMounted = false; };
  }, [pdfFile, formJson.pdfWidth, formJson.pdfHeight, mode]);

  useEffect(() => {
    if (svgElement && svgMountRef.current) {
      svgMountRef.current.innerHTML = '';
      svgMountRef.current.appendChild(svgElement);
      if (showGuidesOverlay && formRef.current) {
        try {
          const mountedSvg = svgMountRef.current.querySelector('svg') as SVGSVGElement | null;
          const g = buildSvgGuides((mountedSvg || svgElement) as unknown as SVGSVGElement | null, formRef.current);
          setExtractedGuides(g);
        } catch {
          setExtractedGuides(null);
        }
      } else {
        setExtractedGuides(null);
      }
      if (showDetectedFrames) recomputeFrames();
    }
  }, [svgElement, showDetectedFrames, recomputeFrames]);

  useEffect(() => {
    if (!showGuidesOverlay) {
      setExtractedGuides(null);
      setSelectedGuideKeys(new Set());
      return;
    }
    if (!svgElement || !svgMountRef.current || !formRef.current) {
      setExtractedGuides(null);
      return;
    }
    try {
      const mountedSvg = svgMountRef.current.querySelector('svg') as SVGSVGElement | null;
      const g = buildSvgGuides((mountedSvg || svgElement) as unknown as SVGSVGElement | null, formRef.current);
      setExtractedGuides(g);
    } catch {
      setExtractedGuides(null);
    }
  }, [showGuidesOverlay, svgElement, imgSize.width, imgSize.height]);

  // removed unused focus effect for fieldNameInputRef

  useEffect(() => {
    if (editingFieldIdx !== null && localFields[editingFieldIdx]) {
      const field = localFields[editingFieldIdx];
      setModalOptionsInput(
        Array.isArray(field.options)
          ? field.options
              .map((o: any) => {
                if (typeof o === 'string') return o;
                const value = String(o?.value ?? '').trim();
                const label = String(o?.label ?? o?.value ?? '').trim();
                if (!value && !label) return '';
                return label && label !== value ? `${value}|${label}` : value;
              })
              .filter(Boolean)
              .join('\n')
          : ''
      );
      setModalFontSize(field.fontsize || 12);
      setModalTextAlign(field.textAlign === 'center' || field.textAlign === 'right' ? field.textAlign : 'left');
    }
  }, [editingFieldIdx]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, type, value } = e.target;
    const isCheckbox = type === 'checkbox' && 'checked' in e.target;
    const isTextarea = (e.target as HTMLElement).tagName.toLowerCase() === 'textarea';
    
    setFieldValues(prev => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value
    }));
  };


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let numberingPrefix = metaFields.numbering_prefix.trim();
    if (numberingPrefix && !numberingPrefix.endsWith('-')) {
      numberingPrefix += '-';
    }
    if (
      !metaFields.form_description.trim() ||
      !metaFields.form_ref.trim() ||
      !numberingPrefix
    ) {
      message.error('Please fill all required fields: Form Description, Form Ref, and Numbering Prefix.');
      return;
    }
    if (!selectedGroupId) {
      message.error('Please select a parent form group.');
      setSubmitting(false);
      return;
    }
    if (hasDuplicateFieldNames) {
      message.error('Duplicate field names detected. Please ensure all field names are unique.');
      return;
    }
    if (mode !== 'edit' && !pdfFile) {
      message.error('Please upload a PDF file.');
      return;
    }
    if (!svgElement) {
      message.error('SVG background not generated.');
      return;
    }
    // --- Restore essential field validation before form insert ---
    const essentialFields = (groupEssentialFields || []).map(f => f && typeof f === 'string' ? f.trim().toLowerCase() : '').filter(Boolean);
    const formFieldNames = localFields.map(f => f.name && typeof f.name === 'string' ? f.name.trim().toLowerCase() : '').filter(Boolean);
    const missingEssentials = essentialFields.filter(f => !formFieldNames.includes(f));
    if (missingEssentials.length > 0) {
      message.error('Missing essential fields: ' + missingEssentials.join(', '));
      setSubmitting(false);
      return;
    }
    setSubmitting(true);
    try {
      // Clone SVG and mask field areas (handle viewBox scaling) before serializing
      const svgForSave = svgElement.cloneNode(true) as SVGSVGElement;
      // Ensure the saved SVG reflects the *current* scaled form size (Option B).
      // (Even if svgElement state lags, imgSize is the source of truth we persist in form_json.)
      try {
        svgForSave.setAttribute('width', String(imgSize.width));
        svgForSave.setAttribute('height', String(imgSize.height));
        svgForSave.style.width = `${imgSize.width}px`;
        svgForSave.style.height = `${imgSize.height}px`;
      } catch {}
      const ns = 'http://www.w3.org/2000/svg';
      const viewBoxAttr = svgForSave.getAttribute('viewBox');
      const widthAttr = parseFloat((svgForSave.getAttribute('width') || '').toString().replace('px', '')) || (formJson.pdfWidth || 600);
      const heightAttr = parseFloat((svgForSave.getAttribute('height') || '').toString().replace('px', '')) || (formJson.pdfHeight || 800);
      let scaleX = 1, scaleY = 1;
      if (viewBoxAttr) {
        const parts = viewBoxAttr.trim().split(/\s+/).map(Number);
        if (parts.length === 4 && parts.every(n => !isNaN(n))) {
          const [, , vbW, vbH] = parts;
          if (widthAttr && heightAttr) {
            scaleX = vbW / widthAttr;
            scaleY = vbH / heightAttr;
          }
        }
      }
      (localFields || []).forEach((f) => {
        const pos = f && f.position;
        if (!pos) return;
        const { x0, y0, x1, y1 } = pos;
        const rect = document.createElementNS(ns, 'rect');
        rect.setAttribute('x', String(x0 * scaleX));
        rect.setAttribute('y', String(y0 * scaleY));
        rect.setAttribute('width', String((x1 - x0) * scaleX));
        rect.setAttribute('height', String((y1 - y0) * scaleY));
        rect.setAttribute('fill', '#ffffff');
        rect.setAttribute('data-mask', 'field-area');
        rect.setAttribute('shape-rendering', 'crispEdges');
        svgForSave.appendChild(rect);
      });
      const svgString = new XMLSerializer().serializeToString(svgForSave);
      const svgFile = new File([svgString], pdfFileName ? pdfFileName.replace(/\.pdf$/i, '.svg') : 'form-background.svg', { type: 'image/svg+xml' });
      // Build meta payload excluding any 'assigned_to' fields
      const metaToSave: any = { ...metaFields };
      // Remove optional assignment-related fields if present
      delete metaToSave.assigned_to_user_id;
      delete metaToSave.assigned_to;
      delete metaToSave.assignedUserId;
      if (mode === 'edit' && definitionId) {
        const formJsonPayload = JSON.stringify({
          formName: formJson.formName,
          fields: localFields,
          pdfWidth: imgSize.width,
          pdfHeight: imgSize.height,
        });
        await updateData(tableName, Number(definitionId), {
          form_json: formJsonPayload,
          svg_file: encodeSvgFileForDb(svgString),
          updated_at: new Date().toISOString(),
        });
        onResult({ success: true, message: 'Form definition updated!' });
      } else {
        await insertDataMultipart(
          tableName,
          {
            ...metaToSave,
            numbering_prefix: numberingPrefix,
            group_id: selectedGroupId, // Ensure group_id is included
            form_json: {
              formName: formJson.formName,
              fields: localFields,
              pdfWidth: imgSize.width,
              pdfHeight: imgSize.height
            },
            svg_filename: svgFile.name,
            svg_file: svgString, // Store SVG as text in DB
          },
          {
            svg_file: svgFile,
          }
        );
        onResult({ success: true, message: 'Form definition saved!' });
      }
    } catch (err) {
      onResult({ success: false, message: 'Failed to save form definition.' });
    } finally {
      setSubmitting(false);
    }
  };



  // Initialize marquee selection
  const lastMarqueeAtRef = useRef<number>(0);
  const suppressClearRef = useRef<boolean>(false);
  const {
    marqueeRect,
    isSelecting,
    onMouseDown: onMarqueeMouseDown,
  } = useMarqueeSelection({
    containerRef: svgContainerRef,
    fields: localFields,
    imgSize,
    onSelectionChange: (selectedIndices) => {
      setSelectedIdxs(new Set(selectedIndices));
      lastMarqueeAtRef.current = Date.now();
    },
    multiSelectKey: 'ctrlKey',
    disabled: drawingLike || showDetectedFrames,
    zoom,
  });

  // Marquee selection for auto-detected frames
  const {
    marqueeRect: framesMarqueeRect,
    isSelecting: isFramesSelecting,
    onMouseDown: onFramesMarqueeMouseDown,
  } = useMarqueeSelection({
    containerRef: svgContainerRef,
    fields: frameLikeFields,
    imgSize,
    onSelectionChange: (selectedIndices) => {
      setSelectedFrameIdxs(new Set(selectedIndices));
      lastMarqueeAtRef.current = Date.now();
    },
    multiSelectKey: 'ctrlKey',
    disabled: !showDetectedFrames || drawingLike,
    zoom,
  });

  // Combine mouse handlers
  const combinedMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Drawing mode has priority
    if (drawingLike) {
      mouseHandlers.onMouseDown(e);
      return;
    }
    // When auto-frames are visible, enable marquee for frames selection
    if (showDetectedFrames) {
      // Use marquee for frames
      onFramesMarqueeMouseDown(e);
      return;
    }
    // Default: field marquee + interactions
    onMarqueeMouseDown(e);
    // Do not start drag here to avoid capturing marquee start
  };

  const { onMouseMove, onMouseUp, onMouseLeave } = mouseHandlers;

  // Track recent drag to avoid click-to-clear immediately after marquee/drawing
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const lastDragAtRef = useRef<number>(0);
  const getEventPoint = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const z = zoom || 1;
    return { x: (e.clientX - rect.left) / z, y: (e.clientY - rect.top) / z };
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((prev: number) => {
      const next = prev * factor;
      if (next < 0.3) return 0.3;
      if (next > 3) return 3;
      return next;
    });
  };

  return (
    <>
      {/* Controls sit in the Upload PDF row. display:contents keeps each button on that flex line. */}
      {toolbarSlot ? createPortal(
      <div style={{ display: 'contents' }}>
         <Button
          type="primary"
          loading={submitting}
          onClick={() => formRef.current?.requestSubmit()}
        >
          {mode === 'edit' ? 'Update' : 'Create'}
        </Button>
        <Tooltip title="Help">
          <Button icon={<AiOutlineQuestionCircle size={16} />} onClick={() => setHelpOpen(true)} />
        </Tooltip>
        <Tooltip title="Scale form">
          <Button
            icon={<LuImageUpscale size={16} />}
            onClick={() => {
              setScalePercent(100);
              setScaleFonts(true);
              setScaleModalOpen(true);
            }}
            disabled={!svgElement}
          />
        </Tooltip>
        <Tooltip title="Undo (Ctrl+Z)">
          <Button icon={<UndoOutlined />} onClick={() => undo()} disabled={!canUndo} />
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Y)">
          <Button icon={<RedoOutlined />} onClick={() => redo()} disabled={!canRedo} />
        </Tooltip>
        <Tooltip title="Preview">
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              try {
                if (!svgElement) {
                  message.warning('SVG background not generated yet.');
                  return;
                }
                const svgString = new XMLSerializer().serializeToString(svgElement);
                const payload = {
                  svg: svgString,
                  fields: localFields,
                  pdfWidth: imgSize.width,
                  pdfHeight: imgSize.height,
                };
                const raw = JSON.stringify(payload);
                localStorage.setItem(FORM_PREVIEW_STORAGE_KEY, raw);
                sessionStorage.setItem(FORM_PREVIEW_STORAGE_KEY, raw);
                window.open('/form-preview', '_blank', 'noopener,noreferrer');
              } catch {
                message.error('Failed to open preview.');
              }
            }}
          />
        </Tooltip>
        <Modal
          open={scaleModalOpen}
          title="Scale form size"
          onCancel={() => setScaleModalOpen(false)}
          onOk={() => {
            const f = (Number(scalePercent) || 100) / 100;
            applyFormScale(f, scaleFonts);
            setScaleModalOpen(false);
          }}
          okText="Apply"
          destroyOnClose
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: '#666' }}>
              Current size: {Math.round(imgSize.width)} × {Math.round(imgSize.height)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 120 }}>Scale (%)</span>
              <InputNumber min={10} max={400} step={5} value={scalePercent} onChange={(v) => setScalePercent(Number(v) || 100)} />
            </div>
            <Checkbox checked={scaleFonts} onChange={(e) => setScaleFonts(e.target.checked)}>
              Scale font sizes too
            </Checkbox>
            <div style={{ fontSize: 12, color: '#999' }}>
              This changes the real saved form size (pdfWidth/pdfHeight) and scales all field rectangles.
            </div>
          </div>
        </Modal>
        <Tooltip title={isDrawingMode ? 'Cancel drawing' : 'Draw field'}>
          <Button
            type={isDrawingMode ? 'primary' : 'default'}
            onClick={() => {
              setIsClipMode(false);
              setIsDrawingMode(!isDrawingMode);
            }}
            icon={<MdDraw size={16} />}
          />
        </Tooltip>
        <Tooltip title={isClipMode ? 'Cancel clip' : 'Clip form'}>
          <Button
            type={isClipMode ? 'primary' : 'default'}
            icon={<ScissorOutlined />}
            onClick={() => {
              if (!svgElement) {
                message.warning('Load a PDF or open a saved definition first.');
                return;
              }
              setIsDrawingMode(false);
              setIsClipMode((v) => !v);
            }}
          />
        </Tooltip>

        <Tooltip title={showGuidesOverlay ? 'Hide guides' : 'Show guides'}>
          <Button
            type={showGuidesOverlay ? 'primary' : 'default'}
            icon={<AiOutlineRadiusUpright size={16} />}
            onClick={() => setShowGuidesOverlay(v => !v)}
          />
        </Tooltip>
        {showGuidesOverlay && selectedGuideKeys.size > 0 && (
          <Tooltip title="Remove selected guides">
            <Button danger icon={<MdOutlineRemoveShoppingCart size={16} />} onClick={removeSelectedGuides} />
          </Tooltip>
        )}
        {suppressedGuides.x.length + suppressedGuides.y.length > 0 && (
          <Tooltip title="Restore removed guides">
            <Button icon={<MdSettingsBackupRestore size={16} />} onClick={restoreRemovedGuides} />
          </Tooltip>
        )}
        <Tooltip title={showDetectedFrames ? 'Hide auto-frames' : 'Show auto-frames'}>
          <Button
            type={showDetectedFrames ? 'primary' : 'default'}
            icon={<MdOutlineInput size={16} />}
            onClick={() => {
              const next = !showDetectedFrames;
              setShowDetectedFrames(next);
              if (next) recomputeFrames();
            }}
          />
        </Tooltip>
        {showDetectedFrames && (
          <Tooltip title="Create fields from selection">
            <Button
              icon={<AiOutlinePushpin size={16} />}
              onClick={() => {
                if (creatingRef.current) return;
                creatingRef.current = true;
                try {
                  if (selectedFrameIdxs.size === 0) {
                    message.info('Select at least one auto-frame.');
                    return;
                  }
                  const existingNames = new Set((localFields || []).map(f => (f.name || '').toLowerCase()));
                  let counter = 1;
                  const nextName = () => {
                    let name = `fill_auto${counter}`;
                    while (existingNames.has(name)) { counter += 1; name = `fill_auto${counter}`; }
                    existingNames.add(name);
                    counter += 1;
                    return name;
                  };
                  const selectedRects = Array.from(selectedFrameIdxs).sort((a,b)=>a-b).map(i => detectedFrames[i]);
                  const additions: Field[] = selectedRects.map((r) => ({
                    name: nextName(),
                    label: '',
                    type: 'text',
                    position: { x0: r.x0, y0: r.y0, x1: r.x1, y1: r.y1, page: 1 },
                    fontsize: 12,
                  }));
                  try { console.log('[AutoFrames] created field names:', additions.map(a => a.name)); } catch {}
                  updateFields(prev => [...prev, ...additions]);
                  message.success(`Created ${additions.length} field${additions.length>1?'s':''}.`);
                  setSelectedFrameIdxs(new Set());
                } finally {
                  creatingRef.current = false;
                }
              }}
            />
          </Tooltip>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Tooltip title="Inset (shrink) in PDF units to apply during Fit to Frame">
            <span style={{ fontSize: 12 }}>Inset</span>
          </Tooltip>
          <InputNumber size="small" min={0} max={10} step={0.5} precision={2} value={inset} onChange={(v)=> setInset(Number(v) || 0)} style={{ width: 80 }} />
        </div>
        {showDetectedFrames && (
          <span style={{ fontSize: 12, color: '#666' }}>
            Tip: Select auto-frames and press Delete to remove them
          </span>
        )}
        {showGuidesOverlay && (
          <span style={{ fontSize: 12, color: '#666' }}>
            Tip: Click guides (Shift/Ctrl for more), then Delete to exclude them from auto-frames
          </span>
        )}
        {isDrawingMode && (
          <span style={{ fontSize: 12, color: '#666' }}>
            Click and drag on the PDF to create a new field
          </span>
        )}
        {isClipMode && (
          <span style={{ fontSize: 12, color: '#666' }}>
            Click and drag to crop the form. Fields move with the crop. Create/Update to save it.
          </span>
        )}
      </div>,
      toolbarSlot
      ) : null}
      <FormBuilderHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <form
        ref={formRef}
        style={{ position: 'relative', width: imgSize.width, height: imgSize.height, margin: 0, background: '#fff', textAlign: 'left', transform: `scale(${zoom})`, transformOrigin: 'top left' }}
        tabIndex={0}
        onSubmit={handleSubmit}
        onKeyDown={(e)=>{
          const k = e.key;
          if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
            const tag = (e.target as HTMLElement).tagName.toLowerCase();
            // Let inputs/textarea handle their own keydown (they already nudge via onKeyDown on the input)
            if (tag === 'input' || tag === 'textarea') return;
            if (focusedIdx !== null) {
              const step = e.ctrlKey && e.shiftKey ? 5 : (e.ctrlKey ? 0.5 : (e.shiftKey ? 10 : 1));
              if (k === 'ArrowLeft') nudgeField(focusedIdx, -step, 0);
              if (k === 'ArrowRight') nudgeField(focusedIdx, step, 0);
              if (k === 'ArrowUp') nudgeField(focusedIdx, 0, -step);
              if (k === 'ArrowDown') nudgeField(focusedIdx, 0, step);
              e.preventDefault();
              e.stopPropagation();
            }
          } else if (k === 'Escape') {
            setSelectedIdxs(new Set());
            setFocusedIdx(null);
            setSelectedFrameIdxs(new Set());
            setSelectedGuideKeys(new Set());
            setIsClipMode(false);
            setContextMenu(p=>({ ...p, open:false }));
            const ae = document.activeElement as HTMLElement | null;
            if (ae && typeof ae.blur === 'function') ae.blur();
            // Move focus to form to avoid auto-refocus on overlay/input
            if (formRef.current) {
              formRef.current.focus();
            }
            e.preventDefault();
            e.stopPropagation();
          } else if (k === 'Delete' || k === 'Backspace') {
            // Delete behavior
            const tag = (e.target as HTMLElement).tagName.toLowerCase();
            if (tag === 'input' || tag === 'textarea') return; // allow text editing
            if (showDetectedFrames && selectedFrameIdxs.size > 0) {
              // Remove selected auto-frames from detection list
              removeFramesByIdxs(selectedFrameIdxs);
              setSelectedFrameIdxs(new Set());
              try { message.success('Removed selected auto-frames'); } catch {}
              e.preventDefault();
              e.stopPropagation();
            } else if (showGuidesOverlay && selectedGuideKeys.size > 0) {
              removeSelectedGuides();
              e.preventDefault();
              e.stopPropagation();
            }
          }
        }}
        onMouseMove={(e)=>{
          const p = getContainerPoint(e);
          setCursorPos(p);
        }}
        onContextMenu={(e)=>{
          e.preventDefault();
          const p = getContainerPoint(e);
          setContextMenu({ x: Math.min(p.x, imgSize.width - 10), y: Math.min(p.y, imgSize.height - 10), open: true });
        }}
      >
        <div
          ref={svgContainerRef}
          className="pdf-form-generator"
          onWheel={handleWheel}
          onMouseEnter={() => { hoveredRef.current = true; }}
          onMouseDown={(e)=>{ suppressClearRef.current = false; mouseDownPosRef.current = getEventPoint(e); if (!drawingLike && formRef.current) { try { formRef.current.focus(); } catch {} } combinedMouseDown(e); }}
          onMouseMove={(e)=>{ onMouseMove(e); }}
          onMouseUp={(e)=>{ 
            onMouseUp(e);
            const p0 = mouseDownPosRef.current;
            if (p0) {
              const p1 = getEventPoint(e);
              const dx = p1.x - p0.x; const dy = p1.y - p0.y;
              const moved = Math.hypot(dx, dy);
              if (moved > 3 || isSelecting || isFramesSelecting || !!drawing) {
                lastDragAtRef.current = Date.now();
                // suppress container click-to-clear until next mousedown
                suppressClearRef.current = true;
              }
            }
            mouseDownPosRef.current = null;
          }}
          onMouseLeave={(e)=>{ hoveredRef.current = false; onMouseLeave(e); }}
          style={{ position: 'relative', width: imgSize.width, height: imgSize.height, cursor: drawingLike ? 'crosshair' : 'default' }}
          onDragStart={(e)=> e.preventDefault()}
          onClick={(e)=>{
            // Avoid clearing right after marquee/drawing completes
            if (isSelecting || isFramesSelecting || !!drawing) return;
            if (suppressClearRef.current) return;
            const now = Date.now();
            if (now - lastDragAtRef.current < 250) return;
            if (now - lastMarqueeAtRef.current < 250) return;
            // If click is on empty area (not on a field or auto-frame), clear selection
            const target = e.target as HTMLElement;
            const isField = target.closest('[data-field-overlay]');
            const isAutoFrame = target.closest('[data-autoframe]') || target.closest('[data-autoframes-overlay]');
            const isGuide = target.closest('[data-guide]') || target.closest('[data-guides-overlay]');
            if (!isField && !isAutoFrame && !isGuide) {
              setSelectedIdxs(new Set());
              setFocusedIdx(null);
              setSelectedFrameIdxs(new Set());
              setSelectedGuideKeys(new Set());
            }
          }}
        >
          <div ref={svgMountRef} style={{ position: 'absolute', left: 0, top: 0, width: imgSize.width, height: imgSize.height, pointerEvents: 'none' }} />
          {showGuidesOverlay && guidesOverlay && (
            <GuidesOverlay
              guides={guidesOverlay}
              selected={selectedGuideKeys}
              onToggle={toggleGuideSelection}
              width={imgSize.width}
              height={imgSize.height}
              interactive={!drawingLike}
            />
          )}
          {/* Drawing rectangle visual feedback */}
          {drawing && (
            <div
              style={{
                position: 'absolute',
                left: Math.min(drawing.startPos.x, drawing.currentPos.x),
                top: Math.min(drawing.startPos.y, drawing.currentPos.y),
                width: Math.abs(drawing.currentPos.x - drawing.startPos.x),
                height: Math.abs(drawing.currentPos.y - drawing.startPos.y),
                border: isClipMode ? '2px dashed #d46b08' : '2px dashed #1890ff',
                backgroundColor: isClipMode ? 'rgba(212, 107, 8, 0.12)' : 'rgba(24, 144, 255, 0.1)',
                pointerEvents: 'none',
                zIndex: 100,
                transition: 'all 0.1s ease-out',
                transformOrigin: 'top left',
              }}
            />
          )}
          {/* Marquee selection overlay for fields */}
          {isSelecting && marqueeRect && !showDetectedFrames && (
            <MarqueeSelection
              rect={{
                left: marqueeRect.left,
                top: marqueeRect.top,
                width: marqueeRect.right - marqueeRect.left,
                height: marqueeRect.bottom - marqueeRect.top,
              }}
            />
          )}
          {/* Marquee selection overlay for auto-frames */}
          {isFramesSelecting && framesMarqueeRect && showDetectedFrames && (
            <MarqueeSelection
              rect={{
                left: framesMarqueeRect.left,
                top: framesMarqueeRect.top,
                width: framesMarqueeRect.right - framesMarqueeRect.left,
                height: framesMarqueeRect.bottom - framesMarqueeRect.top,
              }}
            />
          )}
          {/* Detected frames overlay inside the same positioned container */}
          {showDetectedFrames && detectedFrames.length > 0 && (
            <AutoFramesOverlay
              frames={detectedFrames}
              selected={selectedFrameIdxs}
              onToggle={(i, e)=>setSelectedFrameIdxs(prev=>{
                const multi = (e as any)?.ctrlKey || (e as any)?.metaKey || (e as any)?.shiftKey;
                if (!multi) {
                  const next = new Set<number>();
                  next.add(i);
                  return next;
                }
                const next = new Set(prev);
                if (next.has(i)) next.delete(i); else next.add(i);
                return next;
              })}
              width={imgSize.width}
              height={imgSize.height}
            />
          )}
          
        </div>
        <ContextMenu
          contextMenu={contextMenu}
          selectedIdxs={selectedIdxs}
          contextRef={contextRef}
          onFitToFrame={fitSelectedToFrame}
          onAlignSelected={alignSelected}
          onEqualizeSelected={equalizeSelected}
          onDistributeSelected={distributeSelected}
          onBulkEdit={() => {
            const idxs = Array.from(selectedIdxs);
            if (idxs.length >= 2) {
              openBulkEdit(idxs);
            }
          }}
          onMergeSelected={mergeSelectedFields}
          onClose={() => setContextMenu(p => ({ ...p, open: false }))}
        />
        <div style={{ pointerEvents: drawingLike ? 'none' : 'auto' }}>
          <FormFieldRenderer
            fields={localFieldsWithRequired}
            fieldValues={fieldValues}
            selectedIdxs={selectedIdxs}
            focusedIdx={focusedIdx}
            hoverIdx={hoverIdx}
            duplicateNames={duplicateNames}
            resizing={resizing}
            onInputChange={handleInputChange}
            onFieldNameIconClick={handleFieldNameIconClick}
            onMouseEnter={(idx) => setHoverIdx(idx)}
            onMouseLeave={(idx) => setHoverIdx(prev => (prev === idx ? null : prev))}
            onToggleSelect={toggleSelect}
            onNudgeKey={handleNudgeKey}
            onStartDrag={startDrag}
            onStartResize={startResize}
            onContextMenu={(e, idx) => {
              e.preventDefault();
              const p = getContainerPoint(e);
              setContextMenu({ x: Math.min(p.x, imgSize.width - 10), y: Math.min(p.y, imgSize.height - 10), open: true });
            }}
            imgSize={imgSize}
            onSetFieldDefault={(name, value) => {
              try {
                // Update localFields defaultValue for persistence
                updateFields(prev => prev.map(f => f.name === name ? { ...f, defaultValue: value } : f));
                // Also update fieldValues so UI reflects immediately
                setFieldValues(prev => ({ ...prev, [name]: value }));
              } catch {}
            }}
          />
        </div>
        {duplicateFieldError && (
          <Alert type="error" message={duplicateFieldError} showIcon style={{ marginBottom: 12 }} />
        )}
      </form>
      
      <FieldEditModal
        open={editingFieldIdx !== null || (bulkEditingIdxs?.length || 0) >= 2}
        editingFieldIdx={editingFieldIdx}
        bulkMode={(bulkEditingIdxs?.length || 0) >= 2}
        bulkCount={bulkEditingIdxs?.length || 0}
        modalFieldName={modalFieldName}
        modalFieldType={modalFieldType}
        modalRequired={modalRequired}
        modalComponent={modalComponent}
        modalOptionsInput={modalOptionsInput}
        modalFontSize={modalFontSize}
        modalTextAlign={modalTextAlign}
        modalOptions={modalOptions}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        onInputChange={handleModalInputChange}
        onRequiredChange={handleModalRequiredChange}
        onFieldTypeChange={setModalFieldType}
        onModalComponentChange={setModalComponent}
        onOptionsInputChange={(e) => setModalOptionsInput(e.target.value)}
        onFontSizeChange={(e) => setModalFontSize(Number(e.target.value) || 12)}
        onTextAlignChange={setModalTextAlign}
        onDeleteField={handleDeleteField}
        onSetFieldDefault={(name, value) => {
          try {
            // Update localFields defaultValue for persistence
            updateFields(prev => prev.map(f => f.name === name ? { ...f, defaultValue: value } : f));
            // Optionally update fieldValues if needed (no change to current value)
            setFieldValues(prev => ({ ...prev }));
          } catch {}
        }}
      />
    </>
  );
};

type GeneratorMode = 'create' | 'edit';
type PdfToReactFormGeneratorProps = {
  mode?: GeneratorMode;
  definitionId?: string;
};

const PdfToReactFormGenerator: React.FC<PdfToReactFormGeneratorProps> = ({ mode = 'create', definitionId }) => {
  const [formJson, setFormJson] = useState<FormJson | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string | null>(null);
  const [toolbarSlot, setToolbarSlot] = useState<HTMLDivElement | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [metaFields, setMetaFields] = useState<MetaFields>({
    form_description: '',
    form_ref: '',
    reference_document: '',
    numbering_prefix: '',
    assigned_to_user_id: null,
  });
  const isMountedRef = React.useRef(true);
  const location = useLocation();
  // Allow opening the generator from a shared route (e.g. /core/general/pdf-to-react)
  // while still targeting a specific module (e.g. ?module=hse).
  const moduleOverride = useMemo(() => {
    try {
      const raw = new URLSearchParams(location.search).get('module') || '';
      const m = raw.trim().toLowerCase();
      // basic allowlist characters to avoid weird paths; modules are simple slugs (hse, qaqc, ...)
      return /^[a-z0-9_]+$/.test(m) ? m : '';
    } catch {
      return '';
    }
  }, [location.search]);
  const effectivePath = moduleOverride ? `/${moduleOverride}` : location.pathname;
  const {
    formGroups,
    groupLoading,
    selectedGroupId,
    setSelectedGroupId,
    groupEssentialFields,
    isGroupSelected,
  } = useFormGroups(effectivePath);
  const path = effectivePath;
  const [tableName, pageTitle] = getFormGeneratorInfoForPath(effectivePath);
  const [form] = Form.useForm();
  // removed unused routeName

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Edit mode: load existing definition to initialize formJson, metaFields, and selected group
  useEffect(() => {
    let cancelled = false;
    const loadDefinition = async () => {
      try {
        if (mode !== 'edit' || !definitionId || !tableName) return;
        const arr = await fetchSqlData(tableName, 'id', definitionId);
        const rec = Array.isArray(arr) ? arr[0] : null;
        if (!rec || cancelled) return;
        const fj = (rec.form_json || {}) as any;
        const nextFormJson: any = {
          formName: fj.formName || rec.form_description || 'Form',
          fields: Array.isArray(fj.fields) ? fj.fields : [],
          pdfWidth: fj.pdfWidth || 1000,
          pdfHeight: fj.pdfHeight || 1400,
        };
        setFormJson(nextFormJson);
        // Prefill meta
        setMetaFields(prev => ({
          ...prev,
          form_description: rec.form_description || prev.form_description,
          form_ref: rec.form_ref || prev.form_ref,
          reference_document: rec.reference_document || prev.reference_document,
          numbering_prefix: rec.numbering_prefix || prev.numbering_prefix,
        }));
        // Preselect group for overlay enablement
        if (rec.group_id) setSelectedGroupId(Number(rec.group_id));
      } catch {}
    };
    loadDefinition();
    return () => { cancelled = true; };
  }, [mode, definitionId, tableName, setSelectedGroupId]);

  useEffect(() => {
    if (tableName === null) {
      setError('No matching form definition table for this route.');
    }
  }, [tableName]);


  const handleFileChange = async (info: any) => {
    const file = info.file.originFileObj;
    if (!file) return;
    setPdfFile(file);
    setPdfFileName(file.name);
    setLoading(true);
    setFormJson(null);
    setError(null);
    try {
      const json = await parsePdfFile(file);
      console.log('[PDF PARSE RESULT]', json);
      if (!isMountedRef.current) return;
      setFormJson(json);
    } catch (err: any) {
      if (!isMountedRef.current) return;
      if (axios.isAxiosError(err)) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          setError('Session expired. Please log in again.');
        } else if (err.response && err.response.status === 500) {
          setError('Server error occurred while parsing PDF.');
        } else {
          setError('Failed to parse PDF.');
        }
      } else {
        setError('Failed to parse PDF.');
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleMetaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const toUpper = new Set(['form_description', 'form_ref', 'reference_document', 'numbering_prefix']);
    const nextVal = toUpper.has(name) ? (value || '').toUpperCase() : value;
    setMetaFields(prev => ({ ...prev, [name]: nextVal }));
  };

  const handleOverlayResult = (result: { success: boolean; message: string }) => {
    if (result.success) {
      message.success(result.message);
    } else {
      message.error(result.message);
    }
  };
  // removed unused baseRoute

  return (
    <div>
      <h1>{mode === 'edit' ? pageTitle.replace('Form Generator', 'Form Definition Modify') : pageTitle}</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {/* If no groups, show blocking message and do not allow form creation */}
      {formGroups.length === 0 && !groupLoading && (
        <Alert
          type="warning"
          showIcon
          message="No form groups exist for this module. Please go to the Form Group Creation page to create a group before generating a form."
          style={{ margin: '24px auto', maxWidth: 520 }}
        />
      )}

      {/* Only show form UI if groups exist */}
      {formGroups.length > 0 && (
        <FormMetaSection
          metaFields={metaFields}
          onMetaChange={handleMetaChange}
          formGroups={formGroups}
          selectedGroupId={selectedGroupId}
          onGroupChange={(val) => setSelectedGroupId(val)}
          groupEssentialFields={groupEssentialFields}
          groupLoading={groupLoading}
          form={form}
        />
      )}

      {/* Only show PDF upload, form overlay, etc. if groups exist */}
      {formGroups.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <Upload
              accept="application/pdf"
              showUploadList={false}
              customRequest={({ file, onSuccess }) => { onSuccess && onSuccess('ok'); }}
              onChange={handleFileChange}
            >
              <Button icon={<UploadOutlined />} size="middle">Upload PDF</Button>
            </Upload>
            <div ref={setToolbarSlot} style={{ display: 'contents' }} />
            {pdfFileName && <span>{pdfFileName}</span>}
          </div>
          {loading && <p>Parsing PDF...</p>}
          {formJson && Array.isArray(formJson.fields) && tableName && isGroupSelected && (
            <DynamicFormOverlay
              formJson={formJson}
              metaFields={metaFields}
              pdfFile={pdfFile}
              pdfFileName={pdfFileName}
              onResult={handleOverlayResult}
              tableName={tableName}
              selectedGroupId={selectedGroupId}
              groupEssentialFields={groupEssentialFields}
              mode={mode}
              definitionId={definitionId}
              toolbarSlot={toolbarSlot}
            />
          )}
          {formJson && !isGroupSelected && (
            <Alert type="warning" showIcon message="Please select a form group before creating a form." style={{ marginBottom: 16, maxWidth: 480 }} />
          )}
        </>
      )}
    </div>
  );
};

export default PdfToReactFormGenerator;